import { PlanVersion } from './planVersion';

export enum PlanId {
  Free = 1,
  Pro = 50, // legacy Startup
  Team = 100, // legacy Pro
  Enterprice = 1000,
}

export enum IntervalType {
  Monthly = 1,
  Annually = 2,
}

export enum Currency {
  JPY = 'jpy',
  USD = 'usd',
}

export const defaultPlanId = PlanId.Team;

export const availablePlanIds = [
  PlanId.Free,
  PlanId.Pro,
  PlanId.Team,
  PlanId.Enterprice,
];

export function isTeamPlanAvailable(planId: PlanId) {
  return planId >= PlanId.Team;
}

export function isProPlan(planId: PlanId) {
  return planId >= PlanId.Pro;
}

export function getPriceText(
  planId: PlanId,
  currency: Currency,
  intervalType: IntervalType,
  currentPlanVersion: PlanVersion | null,
) {
  const data = {
    [`plan-${PlanId.Free}`]: {
      [Currency.JPY]: {
        [`interval-${IntervalType.Annually}`]: '¥0',
        [`interval-${IntervalType.Monthly}`]: '¥0',
      },
      [Currency.USD]: {
        [`interval-${IntervalType.Annually}`]: '$0',
        [`interval-${IntervalType.Monthly}`]: '$0',
      },
    },
    [`plan-${PlanId.Pro}`]:
      currentPlanVersion && currentPlanVersion < PlanVersion.V2
        ? {
            [Currency.JPY]: {
              [`interval-${IntervalType.Annually}`]: '-',
              [`interval-${IntervalType.Monthly}`]: '¥891',
            },
            [Currency.USD]: {
              [`interval-${IntervalType.Annually}`]: '-',
              [`interval-${IntervalType.Monthly}`]: '$10',
            },
          }
        : {
            [Currency.JPY]: {
              [`interval-${IntervalType.Annually}`]: '¥800',
              [`interval-${IntervalType.Monthly}`]: '¥1,000',
            },
            [Currency.USD]: {
              [`interval-${IntervalType.Annually}`]: '$8',
              [`interval-${IntervalType.Monthly}`]: '$10',
            },
          },
    [`plan-${PlanId.Team}`]:
      currentPlanVersion && currentPlanVersion < PlanVersion.V2
        ? {
            [Currency.JPY]: {
              [`interval-${IntervalType.Annually}`]: '-',
              [`interval-${IntervalType.Monthly}`]: '¥1,200',
            },
            [Currency.USD]: {
              [`interval-${IntervalType.Annually}`]: '-',
              [`interval-${IntervalType.Monthly}`]: '$14',
            },
          }
        : {
            [Currency.JPY]: {
              [`interval-${IntervalType.Annually}`]: '¥1,200',
              [`interval-${IntervalType.Monthly}`]: '¥1,500',
            },
            [Currency.USD]: {
              [`interval-${IntervalType.Annually}`]: '$12',
              [`interval-${IntervalType.Monthly}`]: '$15',
            },
          },
    [`plan-${PlanId.Enterprice}`]: {
      [Currency.JPY]: {
        [`interval-${IntervalType.Annually}`]: '',
        [`interval-${IntervalType.Monthly}`]: '',
      },
      [Currency.USD]: {
        [`interval-${IntervalType.Annually}`]: '',
        [`interval-${IntervalType.Monthly}`]: '',
      },
    },
  };
  const plan = data[`plan-${planId}`];
  if (!plan) return null;
  const planCurrency = plan[currency];
  if (!planCurrency) return null;
  const planCurrencyInterval = planCurrency[`interval-${intervalType}`];
  return planCurrencyInterval || null;
}

export function getPlanName(planId: PlanId, planVersion: PlanVersion | null) {
  let name = null;
  if (planId === PlanId.Free) {
    name = 'Free';
  } else if (planId === PlanId.Team) {
    if (planVersion === PlanVersion.V1) {
      name = 'Pro';
    } else {
      name = 'Team';
    }
  } else if (planId === PlanId.Pro) {
    if (planVersion === PlanVersion.V1) {
      name = 'Startup';
    } else {
      name = 'Pro';
    }
  } else if (planId === PlanId.Enterprice) {
    name = 'Enterprise';
  }
  return name;
}

export function getPlanVersionDateText(planVersion: PlanVersion) {
  if (planVersion === PlanVersion.V1) {
    return '(~2022.12)';
  } else {
    return '';
  }
}

export function getIntervalName(intervalType: IntervalType) {
  let name = null;
  if (intervalType === IntervalType.Annually) {
    name = 'Annually';
  } else if (intervalType === IntervalType.Monthly) {
    name = 'Monthly';
  }
  return name;
}

export function getPriceId(
  planId: PlanId,
  planVersion: PlanVersion,
  intervalType: IntervalType,
  currency: Currency,
) {
  const p = getPlanName(planId, planVersion);
  if (!p) return null;
  const i = getIntervalName(intervalType);
  if (!i) return null;
  const key = [
    'STRIPE_PRICE_ID',
    planId,
    currency.toUpperCase(),
    i.toUpperCase(),
    `V${planVersion}`,
  ].join('_');
  console.log(key);
  return process.env[key];
}

export function getNewSubscriptionItemData(
  item: any,
  currentPlanPriceId: string,
  newPlanPriceId: string,
  intervalType: IntervalType,
  newQuantity: number,
) {
  const itemPriceId = item.price.id;
  const isUpdatePrice = currentPlanPriceId !== newPlanPriceId;
  let setQuantity = item.quantity;
  let setPriceId;
  if (itemPriceId === currentPlanPriceId) {
    setQuantity = newQuantity;
    if (isUpdatePrice) {
      setPriceId = newPlanPriceId;
    }
  } else if (
    itemPriceId === process.env['STRIPE_PRICE_ID_COMMUNITY_JPY_MONTHLY_V1'] &&
    intervalType === IntervalType.Annually
  ) {
    setPriceId = process.env['STRIPE_PRICE_ID_COMMUNITY_JPY_ANNUALLY_V1'];
  } else if (
    itemPriceId === process.env['STRIPE_PRICE_ID_SMS_JPY_MONTHLY_V1'] &&
    intervalType === IntervalType.Annually
  ) {
    setPriceId = process.env['STRIPE_PRICE_ID_SMS_JPY_ANNUALLY_V1'];
  } else if (
    itemPriceId === process.env['STRIPE_PRICE_ID_ACCOUNT_JPY_MONTHLY_V1'] &&
    intervalType === IntervalType.Annually
  ) {
    setPriceId = process.env['STRIPE_PRICE_ID_ACCOUNT_JPY_ANNUALLY_V1'];
  } else if (
    itemPriceId === process.env['STRIPE_PRICE_ID_IP_BANNING_JPY_MONTHLY_V1'] &&
    intervalType === IntervalType.Annually
  ) {
    setPriceId = process.env['STRIPE_PRICE_ID_IP_BANNING_JPY_ANNUALLY_V1'];
  } else {
    setPriceId = itemPriceId;
  }

  return {
    id: item.id,
    quantity: setQuantity,
    price: setPriceId,
  };
}

export function validateForPlan(planId: PlanId, tables: boolean[]) {
  switch (planId) {
    case PlanId.Free:
      return tables[0];
    case PlanId.Pro:
      return tables[1];
    case PlanId.Team:
      return tables[2];
    case PlanId.Enterprice:
      return tables[3];
    default:
  }
  return false;
}
